// These refs are created once and shared across all usages of the composable.
import { DateTime } from "luxon"

const businessDetails = ref(null)
const operatingHours = ref([])
const businessSettings = ref(null)
const isLoadingContext = ref(false)
const contextError = ref(null)

let hasFetchedForBusinessId = null // Tracks the ID for which data was last fetched

export function useBusinessContext() {
  async function fetchBusinessContext(businessId, forceFetch = false) {
    const businessIdStr = String(businessId) // Ensure comparison is consistent
    if (hasFetchedForBusinessId === businessIdStr && !forceFetch && businessDetails.value && String(businessDetails.value.id) === businessIdStr) {
      console.log("Using cached business context for businessId:", businessIdStr)

      return
    }

    isLoadingContext.value = true
    contextError.value = null

    // hasFetchedForBusinessId will be set on successful fetch or fallback

    try {
      console.log(`Fetching business context for ID: ${businessIdStr}...`)

      const { data: responseDataRef, error: apiErrorRef, pending } = await useApi( // useApi returns useFetch's result
        `/api/public/business-context/${businessIdStr}`,
        {},
        { method: 'GET' },
        1, // useFetch
      )


      // Error from useFetch is a ref
      if (apiErrorRef.value) {
        const errorData = apiErrorRef.value.data // Nuxt error object might have data
        const errorMessage = errorData?.message || apiErrorRef.value.message || 'Failed to fetch business context'
        throw new Error(errorMessage)
      }

      // Data from useFetch is also a ref
      const apiPayload = responseDataRef.value

      if (apiPayload && apiPayload.data) { // Assuming your API wraps the context in a 'data' object
        const ctx = apiPayload.data

        businessDetails.value = ctx.details || {}

        operatingHours.value = ctx.operatingHours.map(ohCamel => {
          // Assuming backend sends: accountBusinessId, serviceType, dayOfWeek, openTime, closeTime, isActive
          return {
            ...ohCamel,
            account_business_id: ohCamel.accountBusinessId, // Map to snake_case
            service_type: ohCamel.serviceType,             // Map to snake_case
            day_of_week: parseInt(ohCamel.dayOfWeek),      // Map and ensure number
            open_time: ohCamel.openTime,                   // Map to snake_case
            close_time: ohCamel.closeTime,                 // Map to snake_case
            is_active: ohCamel.isActive !== undefined ? !!ohCamel.isActive : true,
          }
        })


        businessSettings.value = ctx.settings || {}
        console.log("Business context fetched successfully:", businessDetails.value?.name)
        hasFetchedForBusinessId = businessIdStr
      } else {
        console.warn(`API response for business context for ID ${businessIdStr} was empty or malformed. Response:`, apiPayload, ". Falling back to mocks.")
        loadMockDataContext(businessIdStr)
        hasFetchedForBusinessId = businessIdStr
      }
    } catch (err) {
      console.error("Error fetching business context:", err)
      contextError.value = err.message || "Could not load business information."
      loadMockDataContext(businessIdStr)
      hasFetchedForBusinessId = businessIdStr
    } finally {
      isLoadingContext.value = false
    }
  }

  /**
     * Checks if the business is currently open for a specific service type.
     * Considers overnight hours.
     * @param {'delivery' | 'pickup' | 'dine_in'} serviceType - The service type to check.
     * @returns {{ isOpen: boolean, message: string, closesAt: DateTime | null }}
     */
  const checkCurrentStatus = serviceType => {
    if (!businessDetails.value || !operatingHours.value || operatingHours.value.length === 0) {
      return { isOpen: false, message: 'Operating hours not available.', closesAt: null }
    }

    const businessZone = businessDetails.value.timezone || 'UTC'
    const nowInBusinessZone = DateTime.now().setZone(businessZone)
    const dayOfWeekNow = nowInBusinessZone.weekday === 7 ? 0 : nowInBusinessZone.weekday
    const dayOfWeekYesterday = nowInBusinessZone.minus({ days: 1 }).weekday === 7 ? 0 : nowInBusinessZone.minus({ days: 1 }).weekday

    const relevantHours = operatingHours.value.filter(
      h => h.isActive &&
                        h.serviceType === serviceType &&
                        (h.dayOfWeek === dayOfWeekNow || h.dayOfWeek === dayOfWeekYesterday),
    )

    let nextOpeningTime = null
    let currentClosingTime = null

    for (const oh of relevantHours) {
      // Create DateTime objects for today and yesterday's potential slots
      const referenceDate = oh.dayOfWeek === dayOfWeekNow ? nowInBusinessZone.startOf('day') : nowInBusinessZone.minus({ days: 1 }).startOf('day')

      const openDateTime = DateTime.fromISO(`${referenceDate.toISODate()}T${oh.openTime}`, { zone: businessZone })
      let closeDateTime = DateTime.fromISO(`${referenceDate.toISODate()}T${oh.closeTime}`, { zone: businessZone })

      if (closeDateTime < openDateTime) { // Overnight hours
        closeDateTime = closeDateTime.plus({ days: 1 })
      }

      // Check if "now" is within this period
      if (nowInBusinessZone >= openDateTime && nowInBusinessZone < closeDateTime) {
        return {
          isOpen: true,
          message: `Open for ${serviceType} until ${closeDateTime.toFormat('hh:mm a')}.`,
          closesAt: closeDateTime, // Return the closing time of the current period
        }
      }

      // If the opening time is in the future today, track it as the next potential opening
      if (openDateTime > nowInBusinessZone) {
        if (!nextOpeningTime || openDateTime < nextOpeningTime) {
          nextOpeningTime = openDateTime
        }
      }
    }

    // If not currently open, find the next opening time
    if (!nextOpeningTime) {
      // If no future opening today, check for the next available day
      for (let i = 1; i <= 7; i++) {
        const nextDay = nowInBusinessZone.plus({ days: i })
        const nextDayOfWeek = nextDay.weekday === 7 ? 0 : nextDay.weekday

        const nextDayHours = operatingHours.value
          .filter(h => h.isActive && h.serviceType === serviceType && h.dayOfWeek === nextDayOfWeek)
          .sort((a, b) => (a.openTime || '').localeCompare(b.openTime || ''))

        if (nextDayHours.length > 0) {
          nextOpeningTime = DateTime.fromISO(`${nextDay.toISODate()}T${nextDayHours[0].openTime}`, { zone: businessZone })
          break
        }
      }
    }


    let message = `Currently closed for ${serviceType}.`
    if (nextOpeningTime) {
      if (nextOpeningTime.hasSame(nowInBusinessZone, 'day')) {
        message = `Closed for ${serviceType}. Opens today at ${nextOpeningTime.toFormat('hh:mm a')}.`
      } else {
        message = `Closed for ${serviceType}. Opens ${nextOpeningTime.toFormat("ccc 'at' hh:mm a")}.`
      }
    }

    return { isOpen: false, message: message, closesAt: null }
  }

  function loadMockDataContext(businessId) {
    // businessDetails.value = { ...MOCK_BUSINESS_DETAILS, id: businessId } // Include the requested ID in mock
    operatingHours.value = [] // Use spread to ensure it's a new array reference if needed
    businessSettings.value = {}
    console.log("Loaded MOCK business context for ID:", businessId)
  }

  function clearContext() {
    console.log("Clearing business context.")
    businessDetails.value = null
    operatingHours.value = []
    businessSettings.value = null
    contextError.value = null
    hasFetchedForBusinessId = null
  }

  return {
    businessDetails: readonly(businessDetails),
    operatingHours: readonly(operatingHours),
    businessSettings: readonly(businessSettings),
    isLoadingContext: readonly(isLoadingContext),
    contextError: readonly(contextError),
    fetchBusinessContext,
    loadMockDataContext,
    clearContext,
    checkCurrentStatus,
  }
}
